import type { ChatContext, ChatResponse } from '@mise/shared';

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface PageInsights {
  alerts: Array<{ type: 'critical' | 'warning' | 'info'; message: string }>;
  quickStats: Array<{ label: string; value: string }>;
  suggestedActions: string[];
}

export async function getPageInsights(
  page: string
): Promise<{ success: boolean; data?: PageInsights; error?: { message: string } }> {
  const token = localStorage.getItem('manager_token');

  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';
    const response = await fetch(`${apiUrl}/v1/chat/insights?page=${encodeURIComponent(page)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('manager_token');
        localStorage.removeItem('manager_refresh_token');
        localStorage.removeItem('manager_user');
        window.location.href = '/login';
      }
      return { success: false, error: { message: 'Failed to get insights' } };
    }

    return await response.json();
  } catch (error) {
    console.error('Insights request failed:', error);
    return { success: false, error: { message: 'Network error' } };
  }
}

export async function sendChatMessage(
  text: string,
  context: Omit<ChatContext, 'restaurantId'>,
  conversationHistory: ConversationMessage[]
): Promise<ChatResponse> {
  const token = localStorage.getItem('manager_token');

  // Create abort controller with 90 second timeout for AI responses
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 90000);

  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'https://gleaming-elegance-production-d593.up.railway.app/api';
    const response = await fetch(`${apiUrl}/v1/chat/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        message: text,
        context,
        conversationHistory,
      }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('manager_token');
        localStorage.removeItem('manager_refresh_token');
        localStorage.removeItem('manager_user');
        window.location.href = '/login';
      }
      const error = await response.json().catch(() => ({}));
      return {
        success: false,
        error: error.error || { code: 'REQUEST_FAILED', message: 'Request failed' },
      };
    }

    return await response.json();
  } catch (error: any) {
    clearTimeout(timeoutId);
    console.error('Chat request failed:', error);

    // Handle timeout specifically
    if (error.name === 'AbortError') {
      return {
        success: false,
        error: { code: 'TIMEOUT', message: 'Request timed out. Please try again.' },
      };
    }

    return {
      success: false,
      error: { code: 'NETWORK_ERROR', message: 'Network request failed. Please check your connection.' },
    };
  }
}
